import React, { useState, useCallback } from 'react';
import { documentsApi, booksApi } from '../lib/api';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Progress } from './ui/progress';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from './ui/card';
import { Badge } from './ui/badge';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from './ui/select';
import { 
    Upload, FileText, Check, AlertCircle, Loader2, BookOpen, 
    FileUp, X, ChevronRight, Sparkles
} from 'lucide-react';
import { toast } from 'sonner';

const DocumentUpload = ({ onBookCreated, onClose }) => {
    const [step, setStep] = useState('upload'); // upload, analyzing, review, creating, done
    const [file, setFile] = useState(null);
    const [uploadProgress, setUploadProgress] = useState(0);
    const [analysisResult, setAnalysisResult] = useState(null);
    const [editedTitle, setEditedTitle] = useState('');
    const [editedGenre, setEditedGenre] = useState('');
    const [error, setError] = useState(null);
    const [createdBook, setCreatedBook] = useState(null);

    const genres = [
        'Fantasy', 'Science Fiction', 'Romance', 'Mystery', 'Thriller', 
        'Horror', 'Literary Fiction', 'Historical Fiction', 'Young Adult', 
        'Non-Fiction', 'Memoir', 'Self-Help', 'Business', 'Biography'
    ];

    const handleFileSelect = useCallback((e) => {
        const selectedFile = e.target.files?.[0];
        if (selectedFile) {
            const name = selectedFile.name.toLowerCase();
            if (!name.endsWith('.docx') && !name.endsWith('.pdf')) {
                setError('Please select a .docx or .pdf file');
                return;
            }
            if (selectedFile.size > 20 * 1024 * 1024) {
                setError('File size must be less than 20MB');
                return;
            }
            setFile(selectedFile);
            setError(null);
        }
    }, []);

    const handleDrop = useCallback((e) => {
        e.preventDefault();
        const droppedFile = e.dataTransfer.files?.[0];
        if (droppedFile) {
            const name = droppedFile.name.toLowerCase();
            if (!name.endsWith('.docx') && !name.endsWith('.pdf')) {
                setError('Please select a .docx or .pdf file');
                return;
            }
            if (droppedFile.size > 20 * 1024 * 1024) {
                setError('File size must be less than 20MB');
                return;
            }
            setFile(droppedFile);
            setError(null);
        }
    }, []);

    const handleUpload = async () => {
        if (!file) return;
        
        setStep('analyzing');
        setError(null);
        
        try {
            const response = await documentsApi.upload(file, (progressEvent) => {
                const progress = Math.round((progressEvent.loaded * 100) / progressEvent.total);
                setUploadProgress(progress);
            });
            
            setAnalysisResult(response.data);
            setEditedTitle(response.data.detected_title || 'Untitled');
            setEditedGenre(response.data.detected_genre || 'Fiction');
            setStep('review');
            toast.success('Document analyzed successfully!');
        } catch (err) {
            setError(err.response?.data?.detail || 'Failed to analyze document');
            setStep('upload');
        }
    };

    const handleCreateBook = async () => {
        if (!analysisResult?.upload_id) return;
        
        setStep('creating');
        setError(null);
        
        try {
            const response = await documentsApi.createBook(
                analysisResult.upload_id,
                editedTitle,
                editedGenre
            );
            
            setCreatedBook(response.data);
            setStep('done');
            toast.success(`Book "${response.data.title}" created with ${response.data.chapters_created} sections!`);
            
            if (onBookCreated) {
                onBookCreated(response.data);
            }
        } catch (err) {
            setError(err.response?.data?.detail || 'Failed to create book');
            setStep('review');
        }
    };

    const getSectionIcon = (type, subtype) => {
        if (type === 'front_matter') return '📖';
        if (type === 'back_matter') return '📚';
        if (subtype === 'prologue') return '🌅';
        if (subtype === 'epilogue') return '🌇';
        if (subtype === 'appendix') return '📎';
        if (subtype === 'glossary') return '📝';
        return '📄';
    };

    const formatWordCount = (count) => {
        if (count >= 1000) return `${(count / 1000).toFixed(1)}k`;
        return count;
    };

    return (
        <div className="space-y-6">
            {/* Step: Upload */}
            {step === 'upload' && (
                <div className="space-y-4">
                    <div className="text-center mb-6">
                        <div className="inline-flex items-center justify-center w-16 h-16 rounded-full bg-accent/10 mb-4">
                            <FileUp className="h-8 w-8 text-accent" />
                        </div>
                        <h2 className="font-serif text-2xl font-semibold">Import Your Manuscript</h2>
                        <p className="text-muted-foreground mt-2">
                            Upload a .docx or .pdf file and our AI will intelligently detect your book's structure
                        </p>
                    </div>

                    <div
                        className={`border-2 border-dashed rounded-xl p-8 text-center transition-colors ${
                            file ? 'border-accent bg-accent/5' : 'border-border hover:border-accent/50 hover:bg-accent/5'
                        }`}
                        onDragOver={(e) => e.preventDefault()}
                        onDrop={handleDrop}
                        data-testid="upload-dropzone"
                    >
                        {file ? (
                            <div className="space-y-3">
                                <div className="inline-flex items-center gap-3 px-4 py-2 bg-background rounded-lg border">
                                    <FileText className="h-5 w-5 text-accent" />
                                    <span className="font-medium">{file.name}</span>
                                    <span className="text-sm text-muted-foreground">
                                        ({(file.size / 1024 / 1024).toFixed(2)} MB)
                                    </span>
                                    <button 
                                        onClick={() => setFile(null)}
                                        className="p-1 hover:bg-muted rounded"
                                    >
                                        <X className="h-4 w-4" />
                                    </button>
                                </div>
                            </div>
                        ) : (
                            <div className="space-y-3">
                                <Upload className="h-10 w-10 mx-auto text-muted-foreground" />
                                <div>
                                    <p className="font-medium">Drag and drop your file here</p>
                                    <p className="text-sm text-muted-foreground">or click to browse</p>
                                </div>
                                <input
                                    type="file"
                                    accept=".docx,.pdf"
                                    onChange={handleFileSelect}
                                    className="absolute inset-0 w-full h-full opacity-0 cursor-pointer"
                                    data-testid="file-input"
                                />
                            </div>
                        )}
                    </div>

                    <div className="flex items-center gap-2 text-sm text-muted-foreground">
                        <Sparkles className="h-4 w-4" />
                        <span>Supported: .docx, .pdf (max 20MB)</span>
                    </div>

                    {error && (
                        <div className="flex items-center gap-2 p-3 bg-destructive/10 text-destructive rounded-lg">
                            <AlertCircle className="h-4 w-4" />
                            <span>{error}</span>
                        </div>
                    )}

                    <div className="flex justify-end gap-3 pt-4">
                        {onClose && (
                            <Button variant="outline" onClick={onClose}>Cancel</Button>
                        )}
                        <Button 
                            onClick={handleUpload} 
                            disabled={!file}
                            data-testid="analyze-btn"
                        >
                            <Sparkles className="h-4 w-4 mr-2" />
                            Analyze Document
                        </Button>
                    </div>
                </div>
            )}

            {/* Step: Analyzing */}
            {step === 'analyzing' && (
                <div className="text-center py-12 space-y-6">
                    <div className="inline-flex items-center justify-center w-16 h-16 rounded-full bg-accent/10">
                        <Loader2 className="h-8 w-8 text-accent animate-spin" />
                    </div>
                    <div>
                        <h2 className="font-serif text-xl font-semibold">Analyzing Your Document</h2>
                        <p className="text-muted-foreground mt-2">
                            Our AI is reading and detecting your book's structure...
                        </p>
                    </div>
                    <Progress value={uploadProgress} className="max-w-xs mx-auto" />
                    <p className="text-sm text-muted-foreground">
                        {uploadProgress < 100 ? 'Uploading...' : 'Processing with AI...'}
                    </p>
                </div>
            )}

            {/* Step: Review */}
            {step === 'review' && analysisResult && (
                <div className="space-y-6">
                    <div className="text-center mb-4">
                        <div className="inline-flex items-center justify-center w-12 h-12 rounded-full bg-success/10 mb-3">
                            <Check className="h-6 w-6 text-success" />
                        </div>
                        <h2 className="font-serif text-xl font-semibold">Document Analyzed</h2>
                        <p className="text-muted-foreground">Review and confirm the detected structure</p>
                    </div>

                    {/* Editable Details */}
                    <Card>
                        <CardHeader className="pb-3">
                            <CardTitle className="text-base">Book Details</CardTitle>
                        </CardHeader>
                        <CardContent className="space-y-4">
                            <div className="grid sm:grid-cols-2 gap-4">
                                <div className="space-y-2">
                                    <Label htmlFor="title">Title</Label>
                                    <Input
                                        id="title"
                                        value={editedTitle}
                                        onChange={(e) => setEditedTitle(e.target.value)}
                                        data-testid="book-title-input"
                                    />
                                </div>
                                <div className="space-y-2">
                                    <Label htmlFor="genre">Genre</Label>
                                    <Select value={editedGenre} onValueChange={setEditedGenre}>
                                        <SelectTrigger data-testid="book-genre-select">
                                            <SelectValue />
                                        </SelectTrigger>
                                        <SelectContent>
                                            {genres.map(g => (
                                                <SelectItem key={g} value={g}>{g}</SelectItem>
                                            ))}
                                        </SelectContent>
                                    </Select>
                                </div>
                            </div>
                            <div className="flex items-center gap-4 text-sm">
                                <Badge variant="secondary">
                                    {formatWordCount(analysisResult.total_word_count)} words
                                </Badge>
                                <Badge variant="secondary">
                                    {analysisResult.sections?.length || 0} sections detected
                                </Badge>
                            </div>
                            {analysisResult.summary && (
                                <p className="text-sm text-muted-foreground italic">
                                    "{analysisResult.summary}"
                                </p>
                            )}
                        </CardContent>
                    </Card>

                    {/* Detected Structure */}
                    <Card>
                        <CardHeader className="pb-3">
                            <CardTitle className="text-base">Detected Structure</CardTitle>
                            <CardDescription>
                                These sections will become chapters in your book
                            </CardDescription>
                        </CardHeader>
                        <CardContent>
                            <div className="space-y-2 max-h-64 overflow-y-auto">
                                {analysisResult.sections?.map((section, index) => (
                                    <div 
                                        key={index}
                                        className="flex items-center justify-between p-3 bg-muted/50 rounded-lg"
                                    >
                                        <div className="flex items-center gap-3">
                                            <span className="text-lg">
                                                {getSectionIcon(section.type, section.subtype)}
                                            </span>
                                            <div>
                                                <p className="font-medium text-sm">{section.title}</p>
                                                <p className="text-xs text-muted-foreground capitalize">
                                                    {section.subtype?.replace('_', ' ')}
                                                </p>
                                            </div>
                                        </div>
                                        <Badge variant="outline" className="text-xs">
                                            ~{formatWordCount(section.estimated_word_count)} words
                                        </Badge>
                                    </div>
                                ))}
                            </div>
                        </CardContent>
                    </Card>

                    {error && (
                        <div className="flex items-center gap-2 p-3 bg-destructive/10 text-destructive rounded-lg">
                            <AlertCircle className="h-4 w-4" />
                            <span>{error}</span>
                        </div>
                    )}

                    <div className="flex justify-between pt-4">
                        <Button variant="outline" onClick={() => { setStep('upload'); setFile(null); }}>
                            Start Over
                        </Button>
                        <Button onClick={handleCreateBook} data-testid="create-book-btn">
                            <BookOpen className="h-4 w-4 mr-2" />
                            Create Book
                            <ChevronRight className="h-4 w-4 ml-1" />
                        </Button>
                    </div>
                </div>
            )}

            {/* Step: Creating */}
            {step === 'creating' && (
                <div className="text-center py-12 space-y-6">
                    <div className="inline-flex items-center justify-center w-16 h-16 rounded-full bg-accent/10">
                        <Loader2 className="h-8 w-8 text-accent animate-spin" />
                    </div>
                    <div>
                        <h2 className="font-serif text-xl font-semibold">Creating Your Book</h2>
                        <p className="text-muted-foreground mt-2">
                            Setting up chapters and importing content...
                        </p>
                    </div>
                </div>
            )}

            {/* Step: Done */}
            {step === 'done' && createdBook && (
                <div className="text-center py-8 space-y-6">
                    <div className="inline-flex items-center justify-center w-16 h-16 rounded-full bg-success/10">
                        <Check className="h-8 w-8 text-success" />
                    </div>
                    <div>
                        <h2 className="font-serif text-xl font-semibold">Book Created!</h2>
                        <p className="text-muted-foreground mt-2">
                            "{createdBook.title}" has been created with {createdBook.chapters_created} sections
                        </p>
                    </div>
                    
                    <Card className="max-w-sm mx-auto">
                        <CardContent className="pt-4">
                            <div className="space-y-2 text-sm">
                                <div className="flex justify-between">
                                    <span className="text-muted-foreground">Genre:</span>
                                    <span>{createdBook.genre}</span>
                                </div>
                                <div className="flex justify-between">
                                    <span className="text-muted-foreground">Word Count:</span>
                                    <span>{formatWordCount(createdBook.total_word_count)}</span>
                                </div>
                                <div className="flex justify-between">
                                    <span className="text-muted-foreground">Chapters:</span>
                                    <span>{createdBook.chapters_created}</span>
                                </div>
                            </div>
                        </CardContent>
                    </Card>

                    <div className="flex justify-center gap-3">
                        {onClose && (
                            <Button variant="outline" onClick={onClose}>
                                Close
                            </Button>
                        )}
                        <Button onClick={() => onBookCreated && onBookCreated(createdBook)}>
                            <BookOpen className="h-4 w-4 mr-2" />
                            Open Book
                        </Button>
                    </div>
                </div>
            )}
        </div>
    );
};

export default DocumentUpload;
